import { t } from "i18next";
import { appConfig } from "../config/appConfig";
import queryString from 'query-string';
import { jwtDecode } from "jwt-decode";

class TokenService {
    // Refresh Token
    getLocalRefreshToken() {
        const user = this.getUser();

        return user?.refresh_token;
    }

    // Access Token
    getLocalAccessToken(tokenType) {
        const user = this.getUser();

        if (!user) {
            return undefined;
        }

        if (tokenType === 'accessToken') {
            return `${user?.token_type} ${user?.access_token}`;
        }

        return `${user?.id_token}`;
    }

    updateLocalAccessToken(token, expiresIn, idToken) {
        const user = this.getUser();

        user.refresh_token = token;
        user.expires_in = expiresIn;
        user.id_token = idToken;
        this.setUser(user);
    }

    // Access Token Without Bearer Key
    getLocalAccessTokenWithoutBearer(tokenType) {
        const user = this.getUser();

        if (!user) {
            return undefined;
        }

        if (tokenType === 'accessToken') {
            return `${user?.access_token}`;
        }

        return `${user?.id_token}`;
    }

    // User based on code response
    getUser() {
        return JSON.parse(sessionStorage.getItem('user'));
    }

    setUser(user) {
        sessionStorage.setItem('user', JSON.stringify(user));
    }

    removeUser() {
        sessionStorage.removeItem('user');
    }

    getMinutesToTokenExpiration() {
        const user = this.getUser();
        if (!user) {
            return undefined;
        }
        
        return ((user?.expires_in - Date.now()) / 1000 / 60);
    }

    // User based on validate response
    getUserInfo() {
        return JSON.parse(sessionStorage.getItem('userInfo'));
    }

    setUserInfo(user) {
        sessionStorage.setItem('userInfo', JSON.stringify(user));
    }

    removeUserInfo() {
        sessionStorage.removeItem('userInfo');
    }

    getUserRecordExist () {
        const userInfo = this.getUserInfo();

        if(!userInfo){
            return undefined
        }

        return userInfo?.isRecord;
    }

     // User private email
    isPrivateEmail () {
        const userInfo = this.getUserInfo();

        if(!userInfo){
            return undefined
        }

        return userInfo?.isPrivateMail;
    }

    // Clear All Session Data
    clearSessionData() {
        //Not Removing Mobile & Nonce Values For Redirection
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('userInfo');
        sessionStorage.removeItem('error');
        sessionStorage.removeItem('state');
    }

    // Error Data
    getErrorRedirectUrl(){
        const url = this.getRedirectUrlOrigin();        
        this.clearSessionData();
        return url;
    }

    getErrorData() {
        return sessionStorage.getItem('error') ? true : false;
    }

    setErrorData(data) {
        sessionStorage.setItem('error', data);
    }

    removeErrorData() {
        sessionStorage.removeItem('error');
    }

    // Cognito State Data
    getRedirectUrl(){
        var url = this.getRedirectUrlPath();

        if(this.getNonceInfo() && this.isValidHttpUrl(url)){
            let nonce = this.getNonceInfo();
            
            let stringToUrl = new URL(url);
            let parsedUrl = queryString.parse(stringToUrl.search);
            
            if (Object.keys(parsedUrl).length !== 0){
                url = `${url}&state=${nonce}`;
            } else {
                url = `${url}/?state=${nonce}`;
            }
        }
        
        this.clearSessionData();
        return url;
    }
    
    getFromMobile(){
        return sessionStorage.getItem('fromMobile') ==='true'? true : false;
    }

    getRequestForEdit(){
        return sessionStorage.getItem('requestForEdit')==='true' ? true : false;
    }

    getNonceInfo(){
        return sessionStorage.getItem('nonce');
    }

    getRedirectUrlOrigin(){
        let urlOrigin;
        const url = this.getRedirectUrlPath();
        
        // Check if the URL is valid
        if (this.isValidHttpUrl(url)) {
            const parsedUrl = queryString.parse(new URL(url).search);
            
            // If parsed URL contains any query parameters
            if (Object.keys(parsedUrl).length > 0) {
                const redirectUri = parsedUrl?.redirect_uri;
                
                if(this.getFromMobile()){
                    // If redirect_uri is mobile format, extracting its deep link url
                    if(this.isValidUrl(redirectUri)){
                        const redirectUrl = new URL(redirectUri);
                        urlOrigin = redirectUrl.protocol + '//' + redirectUrl.hostname; 
                    }
                } else {
                    // If redirect_uri is valid, extract its origin
                    if (this.isValidHttpUrl(redirectUri)) {
                        const redirectUrl = new URL(redirectUri);
                        urlOrigin = redirectUrl.origin;
                    }
                }
            }
        }
        
        return urlOrigin;
    }

    isValidUrl(string) {
        try {
            new URL(string);
            return true;
        } catch (err) {
            return false;
        }
    }

    isValidHttpUrl(url) {
        try {
            const newUrl = new URL(url);
            return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
        } catch (err) {
            return false;
        }
    }

    getRedirectUrlPath(){
        return sessionStorage.getItem('callback');
    }

    setStateData(data) {
        let parsedData = JSON.parse(data);

        if(parsedData?.app?.toLowerCase().includes('mobile')){
            sessionStorage.setItem('fromMobile','true');
        }else{
            sessionStorage.setItem('fromMobile','false');
        }

        if(parsedData?.action?.toLowerCase().includes('edit')){
            sessionStorage.setItem('requestForEdit','true');
        }else{
            sessionStorage.setItem('requestForEdit','false');
        }

        if('callback' in parsedData === true){
            sessionStorage.setItem('callback', parsedData?.callback);
        }

        if('nonce' in parsedData === true){
            sessionStorage.setItem('nonce', parsedData?.nonce);
        }
    }

    removeStateData() {
        sessionStorage.removeItem('state');
    }

    extractIdentitiesFromJwt(jwtToken) {
        var identitiesInfo;

        const decoded = jwtDecode(jwtToken);
        const { identities } = decoded ?? {};

        if (identities?.length > 0) {
            const { userId, providerType } = identities[0];
            identitiesInfo = { userId, providerType };
        }
        
        return identitiesInfo;

    }
}
export default new TokenService();
